import { useCallback, useState } from "react";
import type {
  AgreementSignature,
  GuestSignature,
} from "../../../../common/src";
import { USER_AGREEMENT, guestSignatureSchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import { trpc } from "../../trpc";
import { toFieldErrors } from "../../utils/fieldErrors";
import { formatRange } from "../../utils/format";
import { AuthLayout } from "../auth/AuthLayout";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles } from "../shared/styles";
import { AgreementSigner } from "./AgreementSigner";

/** Opened from a trip's guest waiver link: each adult aboard signs before departure. */
export const GuestWaiverPage = ({ token }: { token: string }) => {
  const form = useFormStyles();
  const [email, setEmail] = useState("");
  const [isAdult, setIsAdult] = useState(false);
  const [minorNames, setMinorNames] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signedAs, setSignedAs] = useState<string | undefined>(undefined);

  const loadTrip = useCallback(
    (signal: AbortSignal) =>
      token
        ? trpc.auth.previewGuestTrip.query({ token }, { signal })
        : Promise.resolve({ valid: false as const }),
    [token]
  );
  const trip = useAsyncData(loadTrip);
  const sign = useAction(
    useCallback(
      (input: GuestSignature) =>
        trpc.auth.signAsGuest.mutate(input).then(() => true),
      []
    )
  );

  const onSign = async (signature: AgreementSignature) => {
    const parsed = guestSignatureSchema.safeParse({
      ...signature,
      token,
      email,
      isAdult,
      minorNames,
    });
    if (!parsed.success) {
      setErrors(toFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    if (await sign.run(parsed.data)) {
      setSignedAs(parsed.data.signedName);
    }
  };

  if (trip.error) {
    return (
      <AuthLayout width={480}>
        <LoadError message={trip.error} onRetry={trip.reload} />
      </AuthLayout>
    );
  }
  if (!trip.data) {
    return (
      <AuthLayout width={480}>
        <Loading label="Loading the trip…" />
      </AuthLayout>
    );
  }
  if (!trip.data.valid) {
    return (
      <AuthLayout width={480}>
        <Notice kind="info">
          This guest waiver link is for a trip that has ended or was cancelled.
          Ask the person who invited you aboard for a current link.
        </Notice>
      </AuthLayout>
    );
  }
  if (signedAs) {
    return (
      <AuthLayout width={520}>
        <h2>Thanks, {signedAs}</h2>
        <Notice kind="success">
          {`You've signed the waiver for ${trip.data.boatName} (${formatRange(trip.data)}). A copy is on its way to ${email.trim().toLowerCase()}.`}
        </Notice>
        <p>
          Every other adult coming along needs to sign with this same link
          before boarding.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout width={760}>
      <h2>Guest waiver</h2>
      <p>
        <strong>{trip.data.borrowerName}</strong> has invited you aboard{" "}
        <strong>{trip.data.boatName}</strong> for {formatRange(trip.data)}.
        Every adult aboard must read and sign this agreement before boarding.
      </p>
      <AgreementSigner
        doc={USER_AGREEMENT}
        submitLabel="Sign waiver"
        pending={sign.pending}
        error={sign.error}
        onSign={onSign}
      >
        <Field
          label="Your email"
          htmlFor="guest-email"
          hint="We'll email you a copy of what you sign."
          error={errors.email}
        >
          <input
            id="guest-email"
            type="email"
            className={form.control}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <label className={form.checkbox}>
          <input
            type="checkbox"
            checked={isAdult}
            onChange={(e) => setIsAdult(e.target.checked)}
          />
          <span>I am 18 or older.</span>
        </label>
        {errors.isAdult && <Notice kind="error">{errors.isAdult}</Notice>}
        <Field
          label="Children under 18 you're signing for (optional)"
          htmlFor="guest-minors"
          hint="As their parent or legal guardian. Leave blank if none."
          error={errors.minorNames}
        >
          <input
            id="guest-minors"
            className={form.control}
            value={minorNames}
            onChange={(e) => setMinorNames(e.target.value)}
          />
        </Field>
      </AgreementSigner>
    </AuthLayout>
  );
};
